import type {
  IAuth0Config,
  IContextSwitcherOptions,
  IMenuItem,
  IMenuOption,
  INavBarOptions,
  ISubmenuSection,
  IUtilityTrayOptions,
  UtilityOptions,
  UtilityTrayMenuItem,
  UtilityTrayMenuSection
} from '../types';

/**
 * The session context attached to every log event the nav bar sends through
 * `@opengov/psc-react-logger`. Faro stores each key as a `session_attr_<key>`
 * attribute on every event, so anything placed here is readable by everyone
 * with access to the log store.
 *
 * Build it from an allowlist of fields that help debug the nav bar. Never add
 * credentials (`auth0Config.auth0token`), callbacks, React components, or
 * caller-supplied nodes. Each field added here ships to the log store for
 * every session on every host that uses the nav bar.
 */
export type LoggerSessionContext = Record<string, string>;

type LoggerSessionContextOptions = Pick<
  INavBarOptions,
  'appName' | 'menuOptions' | 'contextSwitcherOptions' | 'utilityTrayOptions'
>;

type LoggedSubmenuSection = Pick<ISubmenuSection, 'title'> & { items?: LoggedMenuItem[] };

type LoggedMenuItem = Pick<IMenuItem, 'id' | 'label' | 'url' | 'openInNewTab' | 'shouldFetchNestedMenuItems'> & {
  submenuSections?: LoggedSubmenuSection[];
};

type LoggedMenuOption = Pick<
  IMenuOption,
  'id' | 'label' | 'url' | 'isActive' | 'openInNewTab' | 'shouldFetchNestedMenuItems' | 'counterAdornmentValue'
> & { submenuSections?: LoggedSubmenuSection[] };

/**
 * Describes how the host configured Auth0 without carrying any of the values
 * that configuration holds. `auth0token` is a live bearer token and
 * `auth0GetAccessToken` resolves one, so only their presence is recorded.
 */
type LoggedAuth0Config = Pick<IAuth0Config, 'auth0domain' | 'auth0audience'> & {
  hasToken: boolean;
  hasGetAccessToken: boolean;
};

type LoggedContextSwitcherOptions = Pick<
  IContextSwitcherOptions,
  | 'userUUID'
  | 'baseUrl'
  | 'activeEntityId'
  | 'activeEntityIdType'
  | 'activeProductKey'
  | 'forcedDefaultApplication'
  | 'forceContextSwitcherVisibility'
  | 'disabled'
> & { auth0Config?: LoggedAuth0Config };

type LoggedUtilityTrayMenuItem = Pick<UtilityTrayMenuItem, 'label' | 'url' | 'dataOg' | 'openInNewTab'>;

type LoggedUtilityTrayMenuSection = Pick<UtilityTrayMenuSection, 'label'> & {
  menuItems?: LoggedUtilityTrayMenuItem[];
};

type LoggedUtilityOptions = Pick<UtilityOptions, 'openInNewTab' | 'badgeContent' | 'badgeVariant' | 'iconUrl'> & {
  menuSections?: LoggedUtilityTrayMenuSection[];
};

type LoggedCustomUtilityOptions = LoggedUtilityOptions & { utilityName: string; stringIcon?: string };

type LoggedUtilityTrayOptions = {
  hasSearch: boolean;
  hasCustomProfileSlot: boolean;
  createNewActionOptions?: { label: string; dataOg?: string }[];
  /** `profileImgUrl` is omitted: it can be a signed URL that carries its own credential. */
  profileSettingsOptions?: { updateProfileUrl?: string; hasSignOut: boolean };
  settingsOptions?: LoggedUtilityOptions;
  helpOptions?: LoggedUtilityOptions;
  reportsOptions?: LoggedUtilityOptions;
  notificationsOptions?: LoggedUtilityOptions;
  customUtilityOptions?: LoggedCustomUtilityOptions;
};

const pickSubmenuSection = (section: ISubmenuSection): LoggedSubmenuSection => ({
  title: section.title,
  items: section.items?.map(pickMenuItem)
});

const pickMenuItem = (item: IMenuItem): LoggedMenuItem => ({
  id: item.id,
  label: item.label,
  url: item.url,
  openInNewTab: item.openInNewTab,
  shouldFetchNestedMenuItems: item.shouldFetchNestedMenuItems,
  submenuSections: item.submenuSections?.map(pickSubmenuSection)
});

const pickMenuOption = (option: IMenuOption): LoggedMenuOption => ({
  id: option.id,
  label: option.label,
  url: option.url,
  isActive: option.isActive,
  openInNewTab: option.openInNewTab,
  shouldFetchNestedMenuItems: option.shouldFetchNestedMenuItems,
  counterAdornmentValue: option.counterAdornmentValue,
  submenuSections: option.submenuSections?.map(pickSubmenuSection)
});

/**
 * The loggable view of an Auth0 config. Use it wherever an `auth0Config` would otherwise be
 * passed to the logger, as session context or as log context.
 */
export const pickAuth0Config = (auth0Config: IAuth0Config | undefined): LoggedAuth0Config | undefined =>
  auth0Config && {
    auth0domain: auth0Config.auth0domain,
    auth0audience: auth0Config.auth0audience,
    hasToken: Boolean(auth0Config.auth0token),
    hasGetAccessToken: typeof auth0Config.auth0GetAccessToken === 'function'
  };

const pickContextSwitcherOptions = (
  options: IContextSwitcherOptions | undefined
): LoggedContextSwitcherOptions | undefined =>
  options && {
    userUUID: options.userUUID,
    baseUrl: options.baseUrl,
    activeEntityId: options.activeEntityId,
    activeEntityIdType: options.activeEntityIdType,
    activeProductKey: options.activeProductKey,
    forcedDefaultApplication: options.forcedDefaultApplication,
    forceContextSwitcherVisibility: options.forceContextSwitcherVisibility,
    disabled: options.disabled,
    auth0Config: pickAuth0Config(options.auth0Config)
  };

const pickUtilityTrayMenuItem = (item: UtilityTrayMenuItem): LoggedUtilityTrayMenuItem => ({
  label: item.label,
  url: item.url,
  dataOg: item.dataOg,
  openInNewTab: item.openInNewTab
});

const pickUtilityTrayMenuSection = (section: UtilityTrayMenuSection): LoggedUtilityTrayMenuSection => ({
  label: section.label,
  menuItems: section.menuItems?.map(pickUtilityTrayMenuItem)
});

const pickUtilityOptions = (options: UtilityOptions | undefined): LoggedUtilityOptions | undefined =>
  options && {
    openInNewTab: options.openInNewTab,
    badgeContent: options.badgeContent,
    badgeVariant: options.badgeVariant,
    iconUrl: options.iconUrl,
    menuSections: options.menuSections?.map(pickUtilityTrayMenuSection)
  };

const pickUtilityTrayOptions = (options: IUtilityTrayOptions | undefined): LoggedUtilityTrayOptions | undefined =>
  options && {
    hasSearch: Boolean(options.searchOptions),
    hasCustomProfileSlot: options.customProfileSlot !== undefined && options.customProfileSlot !== null,
    createNewActionOptions: options.createNewActionOptions?.map(({ label, dataOg }) => ({ label, dataOg })),
    profileSettingsOptions: options.profileSettingsOptions && {
      updateProfileUrl: options.profileSettingsOptions.updateProfileUrl,
      hasSignOut: typeof options.profileSettingsOptions.handleSignOut === 'function'
    },
    settingsOptions: pickUtilityOptions(options.settingsOptions),
    helpOptions: pickUtilityOptions(options.helpOptions),
    reportsOptions: pickUtilityOptions(options.reportsOptions),
    notificationsOptions: pickUtilityOptions(options.notificationsOptions),
    customUtilityOptions: options.customUtilityOptions && {
      ...pickUtilityOptions(options.customUtilityOptions),
      utilityName: options.customUtilityOptions.utilityName,
      stringIcon: options.customUtilityOptions.stringIcon
    }
  };

/**
 * `JSON.stringify(undefined)` is `undefined`, not a string, so an absent prop
 * must drop its attribute instead of recording a non-string value.
 */
const serialize = (value: unknown): string | undefined => (value === undefined ? undefined : JSON.stringify(value));

/**
 * Builds the `sessionContext` for the nav bar's `LoggerProvider` from the
 * nav bar props, keeping only the allowlisted fields above. The attribute
 * names (`menuOptions`, `contextSwitcherOptions`, `utilityTrayOptions`) are
 * unchanged so existing log queries keep working; only their contents shrink.
 * A prop the host did not pass produces no attribute at all.
 *
 * `unstable_ogAgentOptions` is deliberately not part of the session context:
 * it carries its own `auth0Config` and host callbacks.
 */
export const buildLoggerSessionContext = ({
  appName,
  menuOptions,
  contextSwitcherOptions,
  utilityTrayOptions
}: LoggerSessionContextOptions): LoggerSessionContext => {
  const attributes: Record<string, string | undefined> = {
    appName,
    menuOptions: serialize(menuOptions?.map(pickMenuOption)),
    contextSwitcherOptions: serialize(pickContextSwitcherOptions(contextSwitcherOptions)),
    utilityTrayOptions: serialize(pickUtilityTrayOptions(utilityTrayOptions))
  };

  const sessionContext: LoggerSessionContext = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== undefined) {
      sessionContext[key] = value;
    }
  }
  return sessionContext;
};
